import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import authApi from "../api/authApi";

export default function Login() {
  const [view, setView] = useState("login"); // 'login' | 'forgot-request' | 'forgot-verify'
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const { login } = useAuth();
  const navigate = useNavigate();

  const changeView = (newView) => {
    setError("");
    setSuccess("");
    setView(newView);
  };

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setLoading(true);
    try {
      const data = await login(phone, password);
      if (data.activeRole === "rider") {
        navigate("/rider", { replace: true });
      } else {
        navigate("/seller", { replace: true });
      }
    } catch (err) {
      setError(err.message || "An unexpected error occurred.");
    } finally {
      setLoading(false);
    }
  };

  const handleForgotRequestSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    if (!phone) {
      setError("Please enter your phone number.");
      return;
    }
    setLoading(true);
    try {
      await authApi.post("/auth/forgot-password", { phone });
      setSuccess("Verification code sent!");
      setView("forgot-verify");
    } catch (err) {
      setError(err.message || "Failed to request password reset.");
    } finally {
      setLoading(false);
    }
  };

  const handleForgotVerifySubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    if (otp.length < 6) {
      setError("Please enter a valid 6-digit OTP code.");
      return;
    }
    if (newPassword.length < 6) {
      setError("New password must be at least 6 characters long.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      await authApi.post("/auth/reset-password", {
        phone,
        otp,
        newPassword,
      });
      setSuccess("Password reset successfully! Please sign in.");
      setPassword("");
      setOtp("");
      setNewPassword("");
      setConfirmPassword("");
      setView("login");
    } catch (err) {
      setError(err.message || "Failed to reset password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-zinc-950 flex font-sans transition-colors">
      {/* Desktop Left Side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-emerald-600 to-teal-900 relative overflow-hidden flex-col justify-center p-16">
        <div className="absolute top-0 right-0 -mt-20 -mr-20 w-80 h-80 bg-white opacity-10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 -mb-20 -ml-20 w-96 h-96 bg-teal-400 opacity-20 rounded-full blur-3xl"></div>

        <div className="relative z-10 max-w-lg">
          <img
            src={`${process.env.PUBLIC_URL}/byka_logo.png`}
            alt="Byka Logo"
            className="h-28 mb-8 object-contain brightness-0 invert"
          />
          <h1 className="text-white text-4xl font-extrabold mb-6 tracking-tight leading-tight">
            Delivery Operations Network
          </h1>
          <p className="text-emerald-50 text-xl leading-relaxed opacity-95">
            The smartest way to connect users and riders in Harare. Reliable,
            fast, and built for your business.
          </p>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-6 sm:p-12 relative bg-gray-50 lg:bg-white lg:dark:bg-zinc-900 transition-colors">
        {/* Mobile Header (Hidden on Desktop) */}
        <div className="lg:hidden absolute top-0 left-0 w-full bg-emerald-500 flex flex-col items-center justify-center px-6 pt-12 pb-24 z-0 rounded-b-[40px] shadow-sm">
          <img
            src={`${process.env.PUBLIC_URL}/byka_logo.png`}
            alt="Byka Logo"
            className="h-20 object-contain mb-3 brightness-0 invert"
          />
          <p className="text-emerald-100 text-sm mt-1">
            Motorcycle Delivery Network
          </p>
        </div>

        {/* Form Card */}
        <div className="w-full max-w-md bg-white dark:bg-zinc-900 rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.08)] lg:shadow-none p-8 z-10 mt-40 lg:mt-0 border border-gray-100 dark:border-zinc-800 lg:border-none transition-colors space-y-6">
          {error && (
            <div className="bg-red-50 dark:bg-red-950/20 border border-red-100 dark:border-red-900/30 text-red-700 dark:text-red-400 text-sm px-4 py-3 rounded-xl transition-all duration-200 flex items-start gap-2.5">
              <svg
                className="w-5 h-5 shrink-0 mt-0.5 text-red-500 dark:text-red-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              <span>{error}</span>
            </div>
          )}

          {success && (
            <div className="bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-100 dark:border-emerald-900/30 text-emerald-700 dark:text-emerald-400 text-sm px-4 py-3 rounded-xl transition-all duration-200 flex items-start gap-2.5">
              <svg
                className="w-5 h-5 shrink-0 mt-0.5 text-emerald-500 dark:text-emerald-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span>{success}</span>
            </div>
          )}
          {/* VIEW: LOGIN */}
          {view === "login" && (
            <>
              <div className="lg:hidden text-center mb-8">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                  Welcome back
                </h2>
                <p className="text-gray-500 dark:text-gray-400 mt-1 text-sm">
                  Sign in to your account
                </p>
              </div>
              <div className="hidden lg:block mb-10">
                <h2 className="text-3xl font-bold text-gray-900 dark:text-white">
                  Welcome back
                </h2>
                <p className="text-gray-500 dark:text-gray-400 mt-2">
                  Please enter your details to sign in.
                </p>
              </div>

              <form onSubmit={handleLoginSubmit} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="07XXXXXXXX"
                    className="w-full px-4 py-3.5 bg-gray-50 dark:bg-zinc-850 border border-gray-200 dark:border-zinc-700 rounded-xl text-gray-900 dark:text-white focus:bg-white dark:focus:bg-zinc-800 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors"
                    required
                  />
                </div>

                <div>
                  <div className="flex justify-between items-center mb-1.5">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Password
                    </label>
                    <button
                      type="button"
                      onClick={() => changeView("forgot-request")}
                      className="text-xs text-emerald-600 dark:text-emerald-400 hover:underline font-semibold"
                    >
                      Forgot Password?
                    </button>
                  </div>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your password"
                    className="w-full px-4 py-3.5 bg-gray-50 dark:bg-zinc-850 border border-gray-200 dark:border-zinc-700 rounded-xl text-gray-900 dark:text-white focus:bg-white dark:focus:bg-zinc-800 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors"
                    required
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-emerald-500 hover:bg-emerald-600 active:bg-emerald-700 text-white font-semibold py-3.5 rounded-xl text-lg transition-all disabled:opacity-70 mt-4 shadow-lg shadow-emerald-500/30"
                >
                  {loading ? "Signing in..." : "Sign In"}
                </button>
              </form>

              <p className="text-center text-gray-500 dark:text-gray-400 mt-8 text-sm">
                Don't have an account?{" "}
                <Link
                  to="/register"
                  className="text-emerald-600 dark:text-emerald-400 font-semibold hover:text-emerald-700 dark:hover:text-emerald-300 transition-colors"
                >
                  Register here
                </Link>
              </p>
            </>
          )}

          {/* VIEW: FORGOT REQUEST */}
          {view === "forgot-request" && (
            <>
              <div className="mb-8">
                <button
                  type="button"
                  onClick={() => changeView("login")}
                  className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 mb-4 transition-colors"
                >
                  <svg
                    className="w-4 h-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 19l-7-7 7-7"
                    />
                  </svg>
                  Back to Sign In
                </button>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                  Reset Password
                </h2>
                <p className="text-gray-500 dark:text-gray-400 mt-1 text-sm leading-relaxed">
                  Enter your registered phone number. We'll send a 6-digit
                  verification code to your mobile device.
                </p>
              </div>

              <form onSubmit={handleForgotRequestSubmit} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="07XXXXXXXX"
                    className="w-full px-4 py-3.5 bg-gray-50 dark:bg-zinc-850 border border-gray-200 dark:border-zinc-700 rounded-xl text-gray-900 dark:text-white focus:bg-white dark:focus:bg-zinc-800 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors"
                    required
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-emerald-500 hover:bg-emerald-600 active:bg-emerald-700 text-white font-semibold py-3.5 rounded-xl text-lg transition-all disabled:opacity-70 mt-4 shadow-lg shadow-emerald-500/30"
                >
                  {loading ? "Sending Code..." : "Send Verification Code"}
                </button>
              </form>
            </>
          )}

          {/* VIEW: FORGOT VERIFY */}
          {view === "forgot-verify" && (
            <>
              <div className="mb-8">
                <button
                  type="button"
                  onClick={() => changeView("forgot-request")}
                  className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 mb-4 transition-colors"
                >
                  <svg
                    className="w-4 h-4 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 19l-7-7 7-7"
                    />
                  </svg>
                  Back
                </button>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                  Enter Verification Code
                </h2>
                <p className="text-gray-500 dark:text-gray-400 mt-1 text-sm leading-relaxed">
                  Enter the 6-digit code sent to{" "}
                  <span className="font-semibold text-gray-700 dark:text-gray-300">
                    {phone}
                  </span>{" "}
                  and choose your new password.
                </p>
              </div>

              <form onSubmit={handleForgotVerifySubmit} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    6-Digit Verification Code
                  </label>
                  <input
                    type="text"
                    maxLength={6}
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.trim())}
                    placeholder="123456"
                    className="w-full px-4 py-3.5 bg-gray-50 dark:bg-zinc-850 border border-gray-200 dark:border-zinc-700 rounded-xl text-gray-900 dark:text-white font-mono tracking-widest text-center text-xl font-bold focus:bg-white dark:focus:bg-zinc-800 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    New Password
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="At least 6 characters"
                      className="w-full px-4 py-3.5 bg-gray-50 dark:bg-zinc-850 border border-gray-200 dark:border-zinc-700 rounded-xl text-gray-900 dark:text-white focus:bg-white dark:focus:bg-zinc-800 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors pr-12"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 p-1"
                    >
                      {showPassword ? (
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858-5.858A9.954 9.954 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m-4.592-4.592a3 3 0 11-4.243-4.243m4.242 4.242L3 3l18 18"
                          />
                        </svg>
                      ) : (
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                          />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    Confirm New Password
                  </label>
                  <input
                    type={showPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Repeat new password"
                    className="w-full px-4 py-3.5 bg-gray-50 dark:bg-zinc-850 border border-gray-200 dark:border-zinc-700 rounded-xl text-gray-900 dark:text-white focus:bg-white dark:focus:bg-zinc-800 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-colors"
                    required
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-emerald-500 hover:bg-emerald-600 active:bg-emerald-700 text-white font-semibold py-3.5 rounded-xl text-lg transition-all disabled:opacity-70 mt-4 shadow-lg shadow-emerald-500/30"
                >
                  {loading ? "Resetting..." : "Reset Password"}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
